import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Loader2, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { WatchStatusBar } from '../components/watch/WatchStatusBar';
import { AddWatchForm } from '../components/watch/AddWatchForm';
import { WatchedLawCard } from '../components/watch/WatchedLawCard';
import { WatchHistory } from '../components/watch/WatchHistory';

// Leyes vigiladas: each user's (and each case's) watched Chilean norms with verifiable official-source checks
export default function WatchedLawsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [matters, setMatters] = useState([]);
  const load = useCallback(async () => { try { setData((await api.get('/watched-laws')).data); } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudieron cargar las leyes vigiladas'); } }, [api]);
  useEffect(() => { load(); api.get('/crm/matters', { params: { limit: 100 } }).then((r) => setMatters(r.data.matters || [])).catch(() => {}); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = {};
  (data?.items || []).forEach((i) => { const k = i.matter_title || 'Sin causa asociada'; (groups[k] = groups[k] || []).push(i); });

  return (
    <div data-testid="watched-laws-page" className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-6xl px-4 pb-40 pt-6 sm:px-6">
        <button data-testid="watched-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />Volver</button>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <h1 className="flex items-center gap-3 text-4xl font-bold tracking-tight sm:text-5xl"><Eye className="h-9 w-9 text-amber-400" />Leyes vigiladas</h1>
          <button data-testid="watched-add-btn" onClick={() => setAdding(!adding)} className="ml-auto flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:opacity-90"><Plus className="h-4 w-4" />Vigilar una norma</button>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">Normas que Fidem citó, las citadas en tus causas y las que agregues. Solo tú ves esta lista. Cada revisión queda registrada con su fecha y su resultado real: éxito o fallo.</p>
        {!data ? <p className="mt-10 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Cargando…</p> : (
          <div className="mt-8 flex flex-col gap-8">
            <WatchStatusBar data={data} onChange={load} />
            {adding && <AddWatchForm catalog={data.catalog} matters={matters} onDone={() => { setAdding(false); load(); }} />}
            {!data.items.length && <p data-testid="watched-empty" className="text-sm text-slate-500">Aún no vigilas normas. Se agregan solas cuando Fidem cita un artículo o guardas una cita en una causa, o puedes agregarlas tú.</p>}
            {Object.entries(groups).map(([g, items]) => (
              <section key={g} data-testid="watched-group" className="flex flex-col gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{g}</h2>
                {items.map((i) => <WatchedLawCard key={i.id} item={i} onChange={load} />)}
              </section>
            ))}
            <WatchHistory refreshKey={data.settings.last_run_at} names={Object.fromEntries(data.items.map((i) => [i.norm_id, i.name]))} />
          </div>
        )}
      </div>
    </div>
  );
}
